import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FiX, FiEdit3, FiMoon, FiSun, FiLoader } from "react-icons/fi";
import MessageContent from "../MessageContent";
import MessageInput from "../MessageInput";
import ActionControllerComponent from "../ActionControllerComponent";
import CitationsComponent from "../CitationsComponent";
import Tooltip from "../Tooltip";
import ErrorBanner from "../ErrorBanner";
import AssistantToastStack from "../AssistantToastStack";
import { AIAssistantAblyProvider, useAssistantAbly } from "../../contexts/AIAssistantAblyContext";
import AssistantSocketListener from "../AssistantSocketListener";
import {
    selectIsChatOpen,
    selectIsChatExpanded,
} from "../../Selectors/AssistantSelector";
import {
    toggleChatVisibility,
    setChatExpanded,
    assistantResponding,
    deleteMessagesKeepingInitial,
    addBulkMessage,
    hideCitations,
    clearCitations,
    clearHistoryMetadata,
    setTaskStatusMessage,
    setHistoryMetadata,
} from "../../state/actions";
import { initiateSession, saveSessionSnapshot } from "../../utils/SessionManager";
import { getConversation } from "../../utils/assistantStore";
import { AI_ASSISTANT_CONSTANTS } from "../../constants/aiAssistantConstants";
import { createRandomId } from "../../utils/randomId";
import { subscribeAssistantToast } from "../../utils/assistantToast";
import "./ChatWindow.scss";

const ChatWindowContent = () => {
    const dispatch = useDispatch();
    const isChatOpen = useSelector(selectIsChatOpen);
    const isExpanded = useSelector(selectIsChatExpanded);
    const messages = useSelector((state) => state.chat.messages);
    const historyMetadata = useSelector((state) => state.chat.historyMetadata);
    const userDetail = useSelector((state) => state.userDetail);
    const {
        citations,
        isActionControllerVisible,
        isCitationsExpanded,
        actionControllerContent,
    } = useSelector((state) => state.chat);
    const hasMessages = messages.length > 0;
    const [clearState, setClearState] = useState(
        AI_ASSISTANT_CONSTANTS.CHAT.CLEAR_STATES.IDLE,
    );
    const isClearingChat =
        clearState === AI_ASSISTANT_CONSTANTS.CHAT.CLEAR_STATES.CLEARING;
    const isNewChatDisabled =
        clearState !== AI_ASSISTANT_CONSTANTS.CHAT.CLEAR_STATES.IDLE;
    const [chatStatusBanner, setChatStatusBanner] = useState(null);
    const [hydratedConversationId, setHydratedConversationId] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const {
        conversationId,
        clientId,
        isSessionLoading,
        isSourceConversationSession,
        sessionInitError,
        realtimeConnectionState,
    } = useAssistantAbly();

    useEffect(() => {
        let isMounted = true;
        const loadMessages = async () => {
            if (!conversationId) return;
            setHydratedConversationId(null);
            console.debug("[AI Assistant] loading messages", { conversationId });
            console.debug("[AI Assistant] current redux messages count", {
                conversationId,
                count: messages.length,
            });
            const snapshot = await getConversation(conversationId);
            if (!isMounted) return;
            const conversationMessages = snapshot?.messages || [];
            console.debug("[AI Assistant] loaded messages", {
                conversationId,
                count: conversationMessages.length,
            });
            dispatch(addBulkMessage(conversationMessages));
            dispatch(setHistoryMetadata(snapshot?.historyMetadata || null));
            setHydratedConversationId(conversationId);
        };
        loadMessages();
        return () => {
            isMounted = false;
        };
    }, [conversationId, dispatch]);

    const isSourceHydrating =
        Boolean(isSourceConversationSession) &&
        (Boolean(isSessionLoading) ||
            (Boolean(conversationId) && hydratedConversationId !== conversationId));
    const hasAssistantIdentity = Boolean(clientId && conversationId);
    const isRealtimeConnected = realtimeConnectionState === "connected";
    const isRealtimeConnecting =
        realtimeConnectionState === "idle" ||
        realtimeConnectionState === "initialized" ||
        realtimeConnectionState === "connecting";
    const isRealtimeRecovering =
        realtimeConnectionState === "disconnected" ||
        realtimeConnectionState === "suspended";
    const hasRealtimeFailure =
        realtimeConnectionState === "failed" ||
        realtimeConnectionState === "closed";
    const hasSessionFailure = Boolean(sessionInitError);
    const isAssistantReady =
        hasAssistantIdentity &&
        !isSessionLoading &&
        !hasSessionFailure &&
        isRealtimeConnected;
    const desiredChatStatusBanner = useMemo(() => {
        if (hasSessionFailure) {
            return {
                kind: "sessionFailure",
                variant: "error",
                message: AI_ASSISTANT_CONSTANTS.ERRORS.SESSION_INIT_BLOCKING,
            };
        }
        if (hasRealtimeFailure) {
            return {
                kind: "realtimeFailure",
                variant: "error",
                message: AI_ASSISTANT_CONSTANTS.ERRORS.REALTIME_CONNECTION_FAILED,
            };
        }
        if (isSourceHydrating) {
            return null;
        }
        if (isSourceConversationSession) {
            return null;
        }
        if (!hasAssistantIdentity || isSessionLoading || isRealtimeConnecting) {
            return {
                kind: "connecting",
                variant: "warning",
                message: AI_ASSISTANT_CONSTANTS.ERRORS.ASSISTANT_CONNECTING,
            };
        }
        if (isRealtimeRecovering || !isAssistantReady) {
            return {
                kind: "reconnecting",
                variant: "warning",
                message: AI_ASSISTANT_CONSTANTS.ERRORS.ASSISTANT_RECONNECTING,
            };
        }
        return null;
    }, [
        hasSessionFailure,
        hasRealtimeFailure,
        isSourceHydrating,
        isSourceConversationSession,
        hasAssistantIdentity,
        isSessionLoading,
        isRealtimeConnecting,
        isRealtimeRecovering,
        isAssistantReady,
    ]);

    useEffect(() => {
        if (!desiredChatStatusBanner) {
            setChatStatusBanner(null);
            return undefined;
        }

        if (desiredChatStatusBanner.variant !== "warning") {
            setChatStatusBanner(desiredChatStatusBanner);
            return undefined;
        }

        const delayMs =
            desiredChatStatusBanner.kind === "reconnecting"
                ? 1500
                : 800;
        const timeoutId = setTimeout(() => {
            setChatStatusBanner(desiredChatStatusBanner);
        }, delayMs);
        return () => clearTimeout(timeoutId);
    }, [desiredChatStatusBanner]);

    useEffect(() => {
        if (!conversationId) return;
        if (hydratedConversationId !== conversationId) return;
        saveSessionSnapshot({
            conversationId,
            clientId,
            messages,
            historyMetadata,
        });
    }, [conversationId, clientId, messages, historyMetadata, hydratedConversationId]);

    const removeToast = useCallback((toastId) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== toastId));
    }, []);

    const scheduleToastRemoval = useCallback((toastId, duration) => {
        if (duration === null) return;
        setTimeout(() => {
            removeToast(toastId);
        }, duration);
    }, [removeToast]);

    const scheduleClearStateReset = () => {
        setTimeout(() => {
            setClearState(AI_ASSISTANT_CONSTANTS.CHAT.CLEAR_STATES.IDLE);
        }, AI_ASSISTANT_CONSTANTS.TOASTS.DURATIONS_MS.SHORT);
    };

    const addToast = useCallback(({
        type = AI_ASSISTANT_CONSTANTS.TOASTS.TYPES.INFO,
        title,
        description,
        compact = false,
        placement = null,
        appearance = null,
        showClose = true,
        badge = null,
        duration = AI_ASSISTANT_CONSTANTS.TOASTS.DURATIONS_MS.DEFAULT,
    }) => {
        const id = createRandomId("toast");
        const hasFiniteDuration =
            typeof duration === "number" && Number.isFinite(duration) && duration > 0;
        const isCountdownBadge =
            typeof badge === "string" &&
            /^\d+s$/i.test(badge.trim()) &&
            hasFiniteDuration;
        const toast = {
            id,
            type,
            title,
            description,
            compact,
            placement: typeof placement === "string" ? placement : null,
            appearance: appearance && typeof appearance === "object" ? appearance : null,
            showClose,
            badge,
            isCountdownBadge,
            expiresAt: isCountdownBadge ? Date.now() + duration : null,
        };
        setToasts((prev) => [...prev, toast]);
        scheduleToastRemoval(id, duration);
        return id;
    }, [scheduleToastRemoval]);

    useEffect(() => {
        return subscribeAssistantToast((toast) => {
            addToast({
                type: toast?.type || AI_ASSISTANT_CONSTANTS.TOASTS.TYPES.INFO,
                title: toast?.title || AI_ASSISTANT_CONSTANTS.TOASTS.TITLES.INFO,
                description: toast?.description,
                compact: Boolean(toast?.compact),
                placement: toast?.placement,
                appearance: toast?.appearance,
                showClose: toast?.showClose !== false,
                badge: toast?.badge || null,
                duration:
                    typeof toast?.duration === "number"
                        ? toast.duration
                        : AI_ASSISTANT_CONSTANTS.TOASTS.DURATIONS_MS.DEFAULT,
            });
        });
    }, [addToast]);

    const handleClearChat = async () => {
        setClearState(AI_ASSISTANT_CONSTANTS.CHAT.CLEAR_STATES.CLEARING);
        try {
            const res = await initiateSession(userDetail?.id);
            if (res?.success) {
                dispatch(deleteMessagesKeepingInitial(0));
                dispatch(clearCitations());
                dispatch(clearHistoryMetadata());
                dispatch(assistantResponding(false));
                dispatch(
                    setTaskStatusMessage({
                        type: AI_ASSISTANT_CONSTANTS.STATUS.TASK_STATUS_END,
                    }),
                );
                setClearState(AI_ASSISTANT_CONSTANTS.CHAT.CLEAR_STATES.IDLE);
                addToast({
                    type: AI_ASSISTANT_CONSTANTS.TOASTS.TYPES.SUCCESS,
                    title: AI_ASSISTANT_CONSTANTS.ACTIONS.NEW_CHAT_TITLE,
                    description: AI_ASSISTANT_CONSTANTS.ACTIONS.NEW_CHAT_DESCRIPTION,
                    duration: AI_ASSISTANT_CONSTANTS.TOASTS.DURATIONS_MS.SHORT,
                });
            } else {
                throw new Error(AI_ASSISTANT_CONSTANTS.ERRORS.CLEAR_CHAT_FAILED);
            }
        } catch (error) {
            setClearState(AI_ASSISTANT_CONSTANTS.CHAT.CLEAR_STATES.FAILED);
                addToast({
                    type: AI_ASSISTANT_CONSTANTS.TOASTS.TYPES.ERROR,
                    title: AI_ASSISTANT_CONSTANTS.ACTIONS.CLEAR_FAILED_TITLE,
                    description: AI_ASSISTANT_CONSTANTS.ACTIONS.CLEAR_FAILED_DESCRIPTION,
                    duration: AI_ASSISTANT_CONSTANTS.TOASTS.DURATIONS_MS.DEFAULT,
                });
            scheduleClearStateReset();
        }
    };

    const toggleCitations = () => {
        if (isCitationsExpanded) {
            dispatch(hideCitations());
        }
    }

    const handleClose = () => {
        dispatch(toggleChatVisibility(false));
        dispatch(setChatExpanded(false));
    };

    const toggleTheme = () => {
        setIsDarkMode((prev) => !prev);
    };

    return (
        <div
            className={` card-container chat-container
    ${isExpanded ? "expanded" : ""}
    ${!isChatOpen ? "collapsed" : ""}
    ${isActionControllerVisible ? "action-controller-visible" : ""}
    ${isDarkMode ? "ai-dark" : ""}`}
        >
            <div className="card-header">
                <div className="card-title">
                    <span>{AI_ASSISTANT_CONSTANTS.CHAT.TITLE}</span>
                </div>

                <div className="card-header-actions">
                    <button
                        type="button"
                        className={`icon-button ${isNewChatDisabled ? "disabled" : ""}`}
                        onClick={!isNewChatDisabled ? handleClearChat : undefined}
                        aria-label={AI_ASSISTANT_CONSTANTS.CHAT.ARIA.START_NEW_CHAT}
                        disabled={isNewChatDisabled}
                    >
                        <Tooltip content={AI_ASSISTANT_CONSTANTS.CHAT.TOOLTIPS.NEW_CHAT} placement="bottom">
                            <span
                                className={isClearingChat ? "new-chat-loader" : ""}
                                data-testid={isClearingChat ? "new-chat-loader" : undefined}
                            >
                                {isClearingChat ? <FiLoader size={16} /> : <FiEdit3 size={16} />}
                            </span>
                        </Tooltip>
                    </button>

                    <button
                        type="button"
                        className="icon-button"
                        onClick={toggleTheme}
                        aria-label={AI_ASSISTANT_CONSTANTS.CHAT.ARIA.TOGGLE_THEME}
                    >
                        <Tooltip
                            content={
                                isDarkMode
                                    ? AI_ASSISTANT_CONSTANTS.CHAT.TOOLTIPS.SWITCH_TO_LIGHT
                                    : AI_ASSISTANT_CONSTANTS.CHAT.TOOLTIPS.SWITCH_TO_DARK
                            }
                            placement="bottom"
                        >
                            <span>
                                {isDarkMode ? <FiSun size={16} /> : <FiMoon size={16} />}
                            </span>
                        </Tooltip>
                    </button>

                    <button
                        type="button"
                        className="icon-button"
                        onClick={handleClose}
                        title={AI_ASSISTANT_CONSTANTS.CHAT.TOOLTIPS.CLOSE}
                        aria-label={AI_ASSISTANT_CONSTANTS.CHAT.ARIA.CLOSE}
                    >
                        <Tooltip content={AI_ASSISTANT_CONSTANTS.CHAT.TOOLTIPS.CLOSE} placement="bottom">
                            <span>
                                <FiX size={16} />
                            </span>
                        </Tooltip>
                    </button>
                </div>
            </div>

            <div className={`chat-body ${hasMessages ? "has-messages" : "no-messages"}`}>
                {chatStatusBanner && (
                    <div className="chat-status-banner" data-testid="chat-status-banner">
                        <ErrorBanner
                            variant={chatStatusBanner.variant}
                            message={chatStatusBanner.message}
                        />
                    </div>
                )}
                <MessageContent isSourceHydrating={isSourceHydrating} />
                <MessageInput
                    isAssistantReady={isAssistantReady}
                />
            </div>
            {isActionControllerVisible && (
                <ActionControllerComponent
                    title={AI_ASSISTANT_CONSTANTS.CHAT.ACTION_CONTROLLER_TITLE}
                    isVisible={isCitationsExpanded}
                    isExpanded={isCitationsExpanded}
                    toggleExpand={toggleCitations}
                >
                    {actionControllerContent === AI_ASSISTANT_CONSTANTS.CHAT.CITATIONS_LABEL && (
                        <CitationsComponent citations={citations} />
                    )}
                </ActionControllerComponent>
            )}
            <AssistantToastStack
                toasts={toasts}
                onClose={removeToast}
                layout={{
                    defaultPosition: "top-right",
                    defaultCompactPosition: "top-right",
                }}
            />
        </div>
    );
};

const ChatWindow = () => (
    <AIAssistantAblyProvider>
        <AssistantSocketListener />
        <ChatWindowContent />
    </AIAssistantAblyProvider>
);

export default ChatWindow;
